import {
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    Menu,
    MenuItem,
    PopoverOrigin,
    Stack,
    styled,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import {
    FileUploadedElsewhere,
    uploadedElsewhereLine,
} from 'modules/common/components/VerifiedStatementUpload/utils/filesUploadedElsewhere';
import {
    formatMonthList,
    formatMonthRangeShort,
} from 'modules/common/components/VerifiedStatementUpload/utils/formatMonthList';
import {
    accountDisplayName,
    isAccountComplete,
    missingPeriods,
    statementAccountLabel,
} from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import CheckSmallIcon from 'modules/common/components/Icons/CheckSmallIcon';
import DotsVerticalIcon from 'modules/common/components/Icons/DotsVerticalIcon';
import RowIconButton from 'modules/common/components/RowIconButton';
import BankLogo from 'modules/HubV2/Bookkeeping/components/Banks2/BankList/Bank/BankLogo';
import bankAccountLabels from 'services/hurdlr/constants/bankAccountLabels';
import { BankAccountSubType } from 'services/hurdlr/queries/useHurdlrPlaidItems/useHurdlrPlaidItems.interface';
import { AccountStatementCoverage } from 'services/bookkeeping/queries/useStatementCoverage';

/** Anchored under the trigger and right-aligned with it. Constant, so hoisted out of render. */
const MENU_ANCHOR_ORIGIN: PopoverOrigin = { vertical: 'bottom', horizontal: 'right' };
const MENU_TRANSFORM_ORIGIN: PopoverOrigin = { vertical: 'top', horizontal: 'right' };

/**
 * Fixed 40px frame for the institution logo.
 *
 * The nested rule is load-bearing and not redundant with `BankLogo`'s own `width`/`height`:
 * `BankLogo` forwards `sx` only in its fallback-icon branch, so the mapped-SVG and image
 * branches need the frame to size them from the outside.
 */
const LogoFrame = styled(Stack)({
    width: 40,
    height: 40,
    flexShrink: 0,
    '& img, & svg': { width: '100%', height: '100%', objectFit: 'contain' },
});

/**
 * Sized so "Upload" and "Manage" occupy the same width — the label swaps when an account
 * completes, and a button that resizes under the pointer reads as a different control.
 */
const ActionButton = styled(Button)({
    minWidth: 101,
    height: 40,
    whiteSpace: 'nowrap',
});

interface StatementAccountCardProps {
    readonly account: AccountStatementCoverage;
    readonly onUploadClick: () => void;
    /** The member has said they can't supply the outstanding months for this account. */
    readonly isAcknowledged?: boolean;
    /** Waiving fires with `true`; the caller confirms before committing. Undoing is immediate. */
    readonly onAcknowledgeChange?: (acknowledged: boolean) => void;
    /** This account has a statement the server has not answered for yet. */
    readonly isProcessing?: boolean;
    /** Files uploaded under another card that hold months on this one. */
    readonly filesUploadedElsewhere?: readonly FileUploadedElsewhere[];
}

/**
 * What the card says about this account's progress.
 *
 * Three shapes, one per stage of the ask, because each stage has a different useful fact. Nothing
 * uploaded: the member needs to know the span and how many that is. Partway: what has landed and
 * precisely which months have not, since a range would no longer describe the gaps — four of
 * seven uploaded says nothing about *which* three are missing. Satisfied: the count alone.
 *
 * `emphasis` is the bold half and moves between them. On the opening state the label leads it,
 * because "Needed" is the instruction; once uploads start it trails, because the outstanding
 * months are the part still to act on.
 *
 * The count is months, never `held_periods.length`. A tracker row exists per document-month, so
 * a PDF carrying several statements holds a row for each and two files can both report the same
 * month — counting rows renders "3 of 2 uploaded", a line that overshoots its own total.
 * Coverage can also credit a month outside the required window, which counting renders as
 * "2 of 1". `missingPeriods` sidesteps both, and is the same definition the Continue gate
 * uses, so the card and the gate cannot disagree about what is outstanding.
 *
 * A waiver only ever renames the outstanding months, never the count. They are still missing —
 * what changed is that the member has answered for them, so asking again would be asking about
 * something already settled. What arrived keeps being stated either way.
 */
function progressStatus(
    account: AccountStatementCoverage,
    isAcknowledged: boolean
): { prefix?: string; emphasis?: string; label: string; color: string } {
    const total = account.required_periods.length;

    // Never a blank line, and never an all-clear: an empty required list means the books start
    // date has not resolved, so "0 of 0 uploaded" would be indistinguishable from a finished
    // account. Same wording as the back-books row, which faces the same state.
    if (total === 0) {
        return { label: 'Statement months not determined yet', color: 'text.secondary' };
    }

    const noun = total === 1 ? 'statement' : 'statements';

    if (isAccountComplete(account)) {
        return { label: `${total} of ${total} ${noun} uploaded`, color: 'success.main' };
    }

    const outstanding = missingPeriods(account);
    const outstandingLabel = isAcknowledged ? 'Skipped' : 'Missing';

    // Nothing in yet, so the whole ask is the news: its span, and how much that is.
    if (outstanding.length === total) {
        return {
            emphasis: `${isAcknowledged ? 'Skipped' : 'Needed'}:`,
            label: ` ${formatMonthRangeShort(account.required_periods)} (${total} ${noun})`,
            color: 'text.primary',
        };
    }

    // Partway. The months are listed rather than ranged: the gaps are not contiguous in general,
    // and a range that spanned them would name months already uploaded.
    return {
        prefix: `Uploaded: ${total - outstanding.length} of ${total} · `,
        emphasis: `${outstandingLabel}: ${formatMonthList(outstanding)}`,
        label: '',
        color: 'text.primary',
    };
}

/**
 * What the action button offers, which is a different question from whether the ask is complete.
 *
 * Three stages, because this button is the member's only route into the modal and each stage wants
 * a different invitation. Nothing credited yet: "Upload statements", and the only stage that earns
 * the filled button. Partway: "Add More", which says the ask is still live without implying it is
 * untouched. Everything held: "Manage", since all that is left inside is revisiting what was filed.
 *
 * The first branch counts *required* months held, not files uploaded. A statement for a month we
 * never asked for credits nothing, so the card still reads as not started — which is what the
 * progress line directly above it already says, and the two must not disagree.
 */
function buttonLabel(account: AccountStatementCoverage): 'Upload statements' | 'Add More' | 'Manage' {
    const heldRequiredCount = account.required_periods.length - missingPeriods(account).length;
    if (heldRequiredCount === 0) {
        return 'Upload statements';
    }
    return isAccountComplete(account) ? 'Manage' : 'Add More';
}

/**
 * One bank account's statement ask, outside the upload modal. A sibling of `DocumentItem`'s
 * bank-statement branch rather than a change to it — this one is coverage-driven and carries
 * the waiver, so folding them together would branch most of the body.
 */
export default function StatementAccountCard({
    account,
    onUploadClick,
    isAcknowledged = false,
    onAcknowledgeChange = undefined,
    isProcessing = false,
    filesUploadedElsewhere = [],
}: Readonly<StatementAccountCardProps>) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    // The same predicate the step's Continue gate uses — a card reading "Manage" while the
    // gate still considers the account outstanding would be a contradiction the member sees.
    const isComplete = isAccountComplete(account);
    const accountTypeLabel = bankAccountLabels[account.account_type as BankAccountSubType] ?? account.account_type;
    const status = progressStatus(account, isAcknowledged);
    const label = buttonLabel(account);
    // Both resolved from the same chain, so a screen reader can never announce a different name
    // from the one on screen. The menu's name carries the mask because it is what tells two cards
    // at one institution apart; `statementAccountLabel` composes that, and knows not to repeat the
    // mask when the name already is the mask.
    const displayName = accountDisplayName(account);
    const menuLabel = statementAccountLabel(account);
    // Only an incomplete account can be waived, and only when we know what to ask for —
    // an empty required list means the books start date isn't resolved, so there is
    // nothing yet to say you don't have. With no item to show, the trigger goes too: a
    // menu that opens onto nothing is worse than no menu.
    const canAcknowledge = !isComplete && account.required_periods.length > 0 && !!onAcknowledgeChange;

    const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget), []);
    const handleClose = useCallback(() => setAnchorEl(null), []);

    /**
     * Drops the anchor the moment the account stops being waivable.
     *
     * Coverage refetches under an open menu: the member's last statement lands, or a waiver
     * completes the account, and `canAcknowledge` flips while the popover is on screen. The
     * trigger unmounts, leaving an anchor pointing at a node no longer in the DOM.
     *
     * The `open` gate above already hides the menu on that render. This is the other half, and
     * both are needed: the gate stops it being seen, and clearing the anchor stops it coming
     * back. An account becomes waivable again whenever a file is deleted from the Manage modal,
     * and a retained anchor would put the menu straight back on screen with no click behind it.
     */
    useEffect(() => {
        if (!canAcknowledge) {
            setAnchorEl(null);
        }
    }, [canAcknowledge]);

    /**
     * One item toggling both ways, so the section's two-way contract is untouched: waiving fires
     * `true` and is confirmed there, undoing fires `false` and lands immediately.
     */
    const handleWaiverClick = useCallback(() => {
        setAnchorEl(null);
        onAcknowledgeChange?.(!isAcknowledged);
    }, [onAcknowledgeChange, isAcknowledged]);

    return (
        <>
            {/* Spacing matches the frame's own padding, so the rule sits the same distance from
                the account above it as from the card edge beside it. */}
            <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <LogoFrame alignItems="center" justifyContent="center">
                        <BankLogo institutionId={account.plaid_institution_id ?? ''} width={40} height={40} />
                    </LogoFrame>

                    <Stack sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h5" color="text.primary">
                            {displayName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {accountTypeLabel} &ensp;•&ensp; {account.account_mask}
                        </Typography>
                    </Stack>

                    <ActionButton
                        variant={label === 'Upload statements' ? 'contained' : 'outlined'}
                        color="secondary"
                        onClick={onUploadClick}
                        // A prefix rather than MUI's `loading`, which disables the button. The
                        // modal is where the processing screen lives, so the one moment a member
                        // most wants to look inside is the one this would lock them out of.
                        startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
                        sx={{ flexShrink: 0 }}>
                        {label}
                    </ActionButton>
                </Stack>

                <Divider />

                <Stack spacing={0.5}>
                    {/* The ⋮ shares the progress line's row, so it stays centred on that line
                        however many file lines sit below it. */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                            {/* The tick reads the state at a glance, before the count is parsed. Only
                                on a satisfied ask — beside an outstanding one it would say the opposite
                                of the number next to it. */}
                            {isComplete && (
                                <CheckSmallIcon
                                    titleAccess="All statements uploaded"
                                    sx={{ fontSize: 16, color: 'success.main' }}
                                />
                            )}
                            <Typography variant="body2" color={status.color}>
                                {status.prefix}
                                {status.emphasis && (
                                    <Typography component="span" variant="body2" color={status.color} fontWeight={600}>
                                        {status.emphasis}
                                    </Typography>
                                )}
                                {status.label}
                            </Typography>
                        </Stack>

                        {canAcknowledge && (
                            <RowIconButton
                                onClick={handleOpen}
                                // Names the account: two cards at one institution otherwise expose two
                                // controls a screen reader reads identically.
                                aria-label={`Statement options for ${menuLabel}`}
                                sx={{ flexShrink: 0 }}
                                disableRipple>
                                <DotsVerticalIcon />
                            </RowIconButton>
                        )}
                    </Stack>

                    {/* A month held here by a file the member uploaded on another card would
                        otherwise turn this card green with no reason given. */}
                    {filesUploadedElsewhere.map((file) => (
                        <Typography key={file.documentUuid} variant="body2" color="text.secondary">
                            {uploadedElsewhereLine(file)}
                        </Typography>
                    ))}
                </Stack>
            </Stack>

            <Menu
                anchorEl={anchorEl}
                // Both, because the effect below runs after paint: for one frame the account is
                // already satisfied while the anchor still stands, and that frame would show a
                // menu whose trigger has just gone. The effect stops it coming back; this stops
                // it being seen at all.
                open={Boolean(anchorEl) && canAcknowledge}
                onClose={handleClose}
                disableAutoFocusItem
                anchorOrigin={MENU_ANCHOR_ORIGIN}
                transformOrigin={MENU_TRANSFORM_ORIGIN}>
                <MenuItem onClick={handleWaiverClick} sx={{ py: 1, px: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {/* Always shown, ticked or not: the item states what the member would be
                            saying, and the box is what says whether they have already said it.
                            The row owns the click, so the box is not a second tab stop. */}
                        <Checkbox
                            checked={isAcknowledged}
                            color="secondary"
                            size="small"
                            inputProps={{ tabIndex: -1 }}
                            sx={{ p: 0 }}
                            disableRipple
                        />
                        <Typography variant="body2">I don&apos;t have these statements</Typography>
                    </Stack>
                </MenuItem>
            </Menu>
        </>
    );
}
